import React from "react";
import {
  Box,
  Text,
  Icon,
  VStack,
  HStack,
  Button,
  Spinner,
  Divider,
} from "@chakra-ui/react";
import { FaFileAlt, FaTrash } from "react-icons/fa";
import { ArtifactProto } from "build-ts/chat_session";
import { NodeInsights } from "containers/Interfaces";
import DataTableComponent from "containers/analysis-plan/DataTableComponent";
import DataFrameDisplayComponent from "components/data_frame/DataFrameDisplayComponent";
import LanggraphMessageDisplayComponent from "./LanggraphMessageDisplayComponent";
import { ArtifactType, artifactTypeFromJSON } from "build-ts/common";
import { KGDataFrameProto } from "build-ts/dataframe";
import { FiLayers } from "react-icons/fi";
import { TbLayoutSidebarRightCollapseFilled } from "react-icons/tb";
import { ScenarioAnalysis } from "build-ts/analyst";
import ScenarioArtifactCard from "./ScenarioArtifactCard";

/** Artifact from chat that includes scenario payload */
export interface ScenarioArtifactProto extends ArtifactProto {
  scenario?: ScenarioAnalysis;
}

/** One scenario entry in the artifacts panel */
export interface ScenarioArtifactItem {
  artifact: ScenarioArtifactProto;
  /** Monotonically-increasing index so we can sort newest-first, same as data artifacts */
  orderIndex: number;
}

interface ArtifactsProps {
  // ── existing data-artifact props ─────────────────────────────────────────
  selectedArtifacts?: {
    artifact?: ArtifactProto;
    nodeInsights?: NodeInsights;
    kgDataFrameData?: KGDataFrameProto;
    langgraphMessage?: string[];
    orderIndex: number;
    loading: boolean;
  }[];
  isLoadingArtifact?: boolean;
  onClosePanel?: () => void;
  onClearAll?: () => void;
  onRemoveArtifact?: (artifact: ArtifactProto) => void;
  onRemoveLanggraphMessage?: () => void;
  /** Map of dataframe_name → source JSON string for the build_dataframe tool call. */
  dataframePlanMap?: Record<string, string>;

  // ── new scenario-artifact props ──────────────────────────────────────────
  /** Scenario draft artifacts produced by the chat agent */
  scenarioArtifacts?: ScenarioArtifactItem[];
  /**
   * Called when the user saves a scenario from a card.
   * The caller is responsible for persisting and updating allArtifacts.
   */
  onScenarioPanelSave?: (scenario: ScenarioAnalysis) => Promise<void>;
  /**
   * Called when the user removes a scenario artifact card (by scenario id string).
   */
  onScenarioPanelRemove?: (scenarioId: string) => void;
  /**
   * Set of scenario ids that are already persisted in the current analyst
   * (used to show "Saved" vs "Draft" badge and toggle Save/Update label).
   */
  savedScenarioIds?: Set<string>;
  /** When set, all dataframes except the one with `except` orderIndex will collapse. */
  collapseAllExcept?: { except: number; tick: number };
}

const Artifacts: React.FC<ArtifactsProps> = ({
  selectedArtifacts = [],
  isLoadingArtifact = false,
  onClosePanel,
  onClearAll,
  onRemoveArtifact,
  onRemoveLanggraphMessage,
  dataframePlanMap = {},
  // scenario props
  scenarioArtifacts = [],
  onScenarioPanelSave,
  onScenarioPanelRemove,
  savedScenarioIds = new Set(),
  collapseAllExcept,
}) => {
  // Sort artifacts by orderIndex (descending) - most recent first
  // Rule :-
  // result is negative, a is before b
  // result is positive, a is after b
  // result is 0, a and b are the same
  const sortedArtifacts = [...selectedArtifacts].sort((a, b) => b.orderIndex - a.orderIndex);

  // Sort scenario artifacts by orderIndex descending as well
  const sortedScenarioArtifacts = [...scenarioArtifacts].sort(
    (a, b) => b.orderIndex - a.orderIndex
  );

  const hasScenarios = sortedScenarioArtifacts.length > 0;
  const hasDataArtifacts = sortedArtifacts.length > 0;
  const hasAnything = hasScenarios || hasDataArtifacts || isLoadingArtifact;

  return (
    <Box height="100%" display="flex" flexDirection="column" bg="white" overflow="hidden">
      {/* ── Header ───────────────────────────────────────── */}
      <HStack
        justifyContent="space-between"
        p={0}
        px={3}
        borderBottom="1px solid"
        borderColor="gray.200"
        bg="white"
        flexShrink={0}
        minHeight="fit-content"
      >
        <HStack>
          <Icon as={FiLayers} boxSize={4} color="purple.700" />
          <Text fontSize="xs" fontWeight="700" color="purple.700" m={0}>
            Artifacts
          </Text>
          {/* Clear All Button – clears data artifacts only */}
          {sortedArtifacts.length > 0 && (
            <Button
              leftIcon={<Icon as={FaTrash} boxSize="2.5" />}
              size="xs"
              colorScheme="red"
              color="gray.500"
              _hover={{ color: "red.500", bg: "red.50" }}
              onClick={onClearAll}
              fontSize="xs"
              iconSpacing={1}
              variant="ghost"
              ml={1}
            >
              Clear
            </Button>
          )}
        </HStack>
        <HStack spacing={2}>
          {/* Close Panel Button */}
          <Button
            size="xs"
            variant="ghost"
            color="gray.500"
            _hover={{ color: "gray.700", bg: "gray.100" }}
            onClick={onClosePanel}
            p={1}
          >
            <Icon as={TbLayoutSidebarRightCollapseFilled} boxSize="6" />
          </Button>
        </HStack>
      </HStack>

      {/* ── Loading Indicator ─────────────────────────────── */}
      {isLoadingArtifact && (
        <Box
          p={2}
          position="absolute"
          top="15%"
          left="50%"
          transform="translate(-50%, -50%)"
          zIndex="25"
          bg="green.100"
          borderRadius={3}
          boxShadow="md"
        >
          <HStack spacing={2}>
            <Spinner size="sm" color="green.600" thickness="2px" />
            <Text mb={0} fontSize="xs" fontWeight="semibold" color="green.700">
              Loading artifact...
            </Text>
          </HStack>
        </Box>
      )}

      {/* ── Content ───────────────────────────────────────── */}
      <Box
        flex="1"
        overflowY="auto"
        p={0}
        css={{
          scrollbarWidth: "thin",
          scrollbarColor: "#CBD5E0 transparent",
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": { background: "#CBD5E0", borderRadius: "3px" },
          "&::-webkit-scrollbar-thumb:hover": { background: "#A0AEC0" },
        }}
      >
        {!hasAnything ? (
          /* Empty state */
          <Box height="100%" display="flex" alignItems="center" justifyContent="center" p={6}>
            <Box textAlign="center">
              <Icon as={FaFileAlt} boxSize={12} color="gray.400" mb={4} />
              <Text color="gray.600" fontSize="lg" mb={2}>
                No Artifacts Selected
              </Text>
              <Text fontSize="sm" color="gray.500">
                Click on artifacts in the chat to view them here
              </Text>
            </Box>
          </Box>
        ) : (
          <VStack spacing={1} width="100%" p={1}>
            {/* ── Scenario Drafts Section ─────────────────── */}
            {hasScenarios && (
              <>
                {sortedScenarioArtifacts.map((item, index) => {
                  const scenario = item.artifact.scenario;
                  if (!scenario) return null;
                  const scenarioId =
                    scenario.id !== undefined && scenario.id !== null
                      ? String(scenario.id)
                      : "";
                  const isInCurrentAnalyst = !!scenarioId && savedScenarioIds.has(scenarioId);

                  // Content fingerprint: when the agent updates a scenario (same id, new content)
                  // this key changes → React remounts the card so form state reinitialises from
                  // the latest props automatically, showing the agent's update side-by-side.
                  const contentFingerprint = [
                    scenario.issue ?? "",
                    scenario.trigger ?? "",
                    scenario.investigation_protocol ?? "",
                    scenario.reporting_instructions ?? "",
                    scenario.analysis_instructions ?? "",
                  ].join("||");
                  const cardKey = `${scenarioId || item.orderIndex}__${contentFingerprint}`;

                  return (
                    <ScenarioArtifactCard
                      key={cardKey}
                      scenario={scenario}
                      isInCurrentAnalyst={isInCurrentAnalyst}
                      isSelected={index === 0}
                      defaultExpanded={true}
                      onSave={async (toSave) => {
                        await onScenarioPanelSave?.(toSave);
                      }}
                      onRemove={() => onScenarioPanelRemove?.(scenarioId)}
                    />
                  );
                })}
                <Divider my={1} />
              </>
            )}
            {/* ── Data Artifacts Section ──────────────────── */}
            {sortedArtifacts.map((item, index) => {
              // Handle langgraph message
              if (item.langgraphMessage) {
                return (
                  <Box
                    key={`langgraph-${item.orderIndex}`}
                    width="100%"
                    bg="white"
                    overflow="hidden"
                  >
                    <LanggraphMessageDisplayComponent
                      langgraphMessage={item.langgraphMessage}
                      onClose={() => onRemoveLanggraphMessage?.()}
                      isSelected={index === 0}
                    />
                    <Divider my={1} />
                  </Box>
                );
              }

              // Handle regular artifacts
              const artifactType = item.artifact
                ? artifactTypeFromJSON(item.artifact.artifact_type)
                : null;

              return (
                <Box
                  key={item.orderIndex}
                  width="100%"
                  overflow="hidden"
                >
                  {/* Render based on artifact type */}
                  {artifactType === ArtifactType.KG_OBSERVATION && item.nodeInsights ? (
                    <DataTableComponent
                      name={"KG Observation"}
                      observationData={item.nodeInsights}
                      onCloseTable={() => onRemoveArtifact?.(item.artifact!)}
                      isSelected={index === 0}
                    />
                  ) : artifactType === ArtifactType.KG_DATAFRAME && item.kgDataFrameData ? (
                    <DataFrameDisplayComponent
                      name={item.kgDataFrameData?.header?.name || "KG DataFrame"}
                      data={item.kgDataFrameData}
                      onClose={() => onRemoveArtifact?.(item.artifact!)}
                      isSelected={index === 0}
                      langgraphMessage={
                        item.kgDataFrameData?.header?.name &&
                          dataframePlanMap[item.kgDataFrameData.header.name]
                          ? [dataframePlanMap[item.kgDataFrameData.header.name]]
                          : undefined
                      }
                      collapseSignal={
                        collapseAllExcept && collapseAllExcept.except !== item.orderIndex
                          ? collapseAllExcept.tick
                          : undefined
                      }
                    />
                  ) : (
                    <Box p={4}>
                      <Text color="gray.500" fontSize="sm">
                        Unable to display artifact
                      </Text>
                    </Box>
                  )}
                  <Divider my={0} mb={0} mt={1} borderColor="white" />
                </Box>
              );
            })}
          </VStack>
        )}
      </Box>
    </Box>
  );
};

export default Artifacts;
